import React from "react";
import { motion } from "framer-motion";
import {
    FaChartLine,
    FaFileInvoice,
    FaBoxes,
    FaTruck,
    FaBalanceScale,
    FaTachometerAlt,
} from "react-icons/fa";
import { easyonDashboardWidgets } from "../../data/easyonPageContent";
import { EasyonReveal } from "./EasyonMotion";

const widgetIcons = {
    quote: FaFileInvoice,
    revenue: FaChartLine,
    inventory: FaBoxes,
    vendor: FaTruck,
    gst: FaBalanceScale,
    performance: FaTachometerAlt,
};

const companies = ["Manufacturing", "Retail", "Trading"] as const;
type OpsCompany = (typeof companies)[number];
const panelByCompany = {
    Manufacturing: ["PO Delays 2", "Raw Material Stock 68%", "Dispatch SLA 96%"],
    Retail: ["Store Replenishment 92%", "Returns 3.1%", "POS Sync Healthy"],
    Trading: ["Open Quotations 31", "Receivables ₹4.8L", "Vendor Lead Time 4.2d"],
} as const;

export const EasyonDashboardSection = () => {
    const [company, setCompany] = React.useState<OpsCompany>("Manufacturing");

    return (
        <section className="eo-dashboard">
            <div className="eo-container">
                <EasyonReveal className="eo-dashboard__header">
                    <span className="eo-eyebrow">Operations Cockpit</span>
                    <h2 className="eo-h2">Multi-Company Dashboard Switcher</h2>
                    <p className="eo-lead">
                        Switch between companies and monitor billing, inventory, vendors, and compliance in real time.
                    </p>
                </EasyonReveal>

                <div className="eo-dashboard-switcher">
                    {companies.map((item) => (
                        <button key={item} type="button" onClick={() => setCompany(item)} className={item === company ? "active" : ""}>
                            {item}
                        </button>
                    ))}
                </div>

                <div className="eo-dashboard__layout">
                    <div className="eo-dashboard__widgets">
                        {easyonDashboardWidgets.map((w, i) => {
                            const Icon = widgetIcons[w.iconKey];
                            return (
                                <motion.div
                                    key={w.label}
                                    className="eo-dashboard__widget"
                                    initial={{ opacity: 0, y: 16 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: false }}
                                    transition={{ delay: i * 0.06 }}
                                >
                                    <div className="eo-dashboard__widget-head">
                                        <Icon size={14} />
                                        <span>{w.label}</span>
                                    </div>
                                    <strong>{w.value}</strong>
                                    <em>{w.change}</em>
                                </motion.div>
                            );
                        })}
                    </div>

                    <motion.div
                        key={company}
                        className="eo-dashboard-board"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.35 }}
                    >
                        <div className="eo-dashboard-board__head">
                            <strong>{company} Control Board</strong>
                            <span>Live sync</span>
                        </div>
                        <div className="eo-dashboard-board__grid">
                            {panelByCompany[company].map((item) => (
                                <div key={item} className="eo-dashboard-board__tile">{item}</div>
                            ))}
                        </div>
                        <div className="eo-dashboard-board__chart">
                            {[38, 56, 42, 66, 74, 58, 84, 76].map((h, i) => <div key={i} style={{ height: `${h}%` }} />)}
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};
